import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import PropTypes from "prop-types";

// Registrar componentes de Chart.js
ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * Componente reutilizable de gráfico circular (Pie Chart)
 * Puede usarse para cualquier tipo de distribución de datos
 */
function PieChart({ 
  data, 
  title = "Distribución",
  height = 300,
  legendPosition = "right",
  showPercentage = true,
  noDataMessage = "No hay datos disponibles",
  colors = null,
  centerLabel = null,
  responsiveLegend = true
}) {
  // Colores por defecto si no se proporcionan
  const defaultColors = [
    "rgba(54, 162, 235, 0.8)", // Azul
    "rgba(153, 102, 255, 0.8)", // Morado
    "rgba(255, 159, 64, 0.8)", // Naranja
    "rgba(40, 167, 69, 0.8)", // Verde
    "rgba(255, 193, 7, 0.8)", // Amarillo
    "rgba(220, 53, 69, 0.8)", // Rojo
    "rgba(23, 162, 184, 0.8)", // Cian
    "rgba(108, 117, 125, 0.8)", // Gris
    "rgba(255, 99, 132, 0.8)", // Rosa
  ];

  const defaultBorderColors = [
    "rgb(54, 162, 235)",
    "rgb(153, 102, 255)",
    "rgb(255, 159, 64)",
    "rgb(40, 167, 69)",
    "rgb(255, 193, 7)",
    "rgb(220, 53, 69)",
    "rgb(23, 162, 184)",
    "rgb(108, 117, 125)",
    "rgb(255, 99, 132)",
  ];

  // Usar colores personalizados o los por defecto
  const backgroundColors = colors?.background || defaultColors;
  const borderColors = colors?.border || defaultBorderColors;

  // Detectar si es móvil para ajustar la posición de la leyenda
  const isMobile = window.innerWidth < 768;
  const finalLegendPosition = responsiveLegend && isMobile ? 'bottom' : legendPosition;

  const chartData = {
    labels: data?.labels || [],
    datasets: [
      {
        data: data?.data || [],
        backgroundColor: backgroundColors,
        borderColor: borderColors,
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: finalLegendPosition,
        labels: {
          usePointStyle: true,
          padding: isMobile ? 10 : 15,
          font: {
            size: isMobile ? 10 : 12,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.parsed || 0;
            
            if (showPercentage) {
              const total = context.dataset.data.reduce((acc, val) => acc + val, 0);
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              return `${label}: ${value} (${percentage}%)`;
            }
            
            return `${label}: ${value}`;
          },
        },
      },
    },
  };

  // Si se proporciona centerLabel, agregar plugin para texto central
  if (centerLabel) {
    options.plugins.centerLabel = {
      display: true,
      text: centerLabel,
    };
  }

  return (
    <div>
      {title && <h5 className="mb-3">{title}</h5>}
      <div style={{ height: `${height}px`, position: "relative" }}>
        {data && data.labels && data.labels.length > 0 ? (
          <Pie data={chartData} options={options} />
        ) : (
          <div className="d-flex align-items-center justify-content-center h-100">
            <div className="text-center">
              <i className="bi bi-pie-chart fs-1 text-muted"></i>
              <p className="text-muted mt-2">{noDataMessage}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

PieChart.propTypes = {
  data: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string),
    data: PropTypes.arrayOf(PropTypes.number),
  }),
  title: PropTypes.string,
  height: PropTypes.number,
  legendPosition: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  showPercentage: PropTypes.bool,
  noDataMessage: PropTypes.string,
  colors: PropTypes.shape({
    background: PropTypes.arrayOf(PropTypes.string),
    border: PropTypes.arrayOf(PropTypes.string),
  }),
  centerLabel: PropTypes.string,
  responsiveLegend: PropTypes.bool,
};

export default PieChart;
